/**
 * Determine if we have to consider to decimal part of the number
 *
 * @param  {number}  number The number
 * @return {boolean}        The answer
 */
const isRealFloat = (number) => {
  // We must round the value in order to resolve the epsilon problem
  // i.e. : 0.1 + 0.7 -> 0.7999999999999999
  const floatValue = Math.round(number * 100) / 100;
  // eslint-disable-next-line no-bitwise
  const intValue = floatValue >> 0;
  return intValue !== floatValue;
};

const pad = (input, length = 2, padString = '0') => {
  const missing = length - input.length;
  const finalInput = input.toString();

  let aChar = '';
  let prefix = '';
  let i = 0;

  if (missing <= 0) {
    return finalInput;
  }

  aChar = padString.charAt(0);

  for (; i < missing; i += 1) {
    prefix += aChar;
  }

  return prefix + input;
};

const toFixed = (value, precision = 2) => {
  let power = 0;
  const finalPrecision = Math.abs(precision);
  power = 10 ** finalPrecision;
  return (Math.round(value * power) / power).toFixed(finalPrecision);
};

const formatNumber = (value, { decimalSeparator, precision, thousandSeparator, groupLength, abbreviate }) => {
  const result = [];

  let str = '';
  let intNum = '';
  let intLength = 0;
  let floatNum = '';
  let floatLength = 0;

  if (abbreviate) {
    str = `${value}`;
  } else {
    str = toFixed(value, precision);
  }

  const pos = str.indexOf('.');

  if (pos !== -1) {
    intNum = str.substring(0, pos);
    floatNum = str.substring(pos + 1);

    // eslint-disable-next-line no-bitwise
    // if ((floatNum >> 0) === 0) {
    //   floatNum = '';
    // } else {
    floatNum += pad('', precision - floatNum.length, '0');
    // }

    intLength = intNum.length;
    floatLength = abbreviate ? groupLength - intLength : precision;

    if (floatLength) {
      floatNum = floatNum.substring(0, floatLength);
    } else {
      floatNum = '';
    }
  } else {
    intNum = str;
    floatNum = '';
    intLength = str.length;
  }

  intNum = pad(intNum, intLength + (groupLength - (intLength % groupLength)), ' ');

  const max = intNum.length;

  for (let i = 0; i < max; i += groupLength) {
    if (i > 0) {
      result.push(thousandSeparator);
    }
    result.push(intNum.substr(i, groupLength));
  }

  if (floatNum) {
    result.push(decimalSeparator, floatNum);
  }

  return result.join('').replace(new RegExp(`^[\\s|${thousandSeparator}]+`), '');
};

let configuration = {
  patterns: {
    standard: ['-n', 'n'],
    amount: ['-n S', 'n S']
  },
  group: {
    separator: ' ',
    length: 3,
    abbr: ['', 'k', 'M', 'Md']
  },
  decimal: {
    separator: ',',
    length: 2
  },
  ordinal: (number) => number + (number === 1 ? 'er' : 'e')
};

const NumberFormatter = {};

NumberFormatter.setConfiguration = function (conf) {
  configuration = conf;
};

NumberFormatter.ordinal = function (value) {
  // eslint-disable-next-line no-bitwise
  const finalValue = value | 0;
  if (value < 1) {
    throw new Error(`first argument must be a positive number: ${finalValue}`);
  }
  return configuration.ordinal.call(null, finalValue);
};

NumberFormatter.formatOdd = function (value) {
  if (typeof value !== 'number') {
    throw new Error(`first argument must be a number: ${value}`);
  }

  const { group, decimal, patterns } = configuration;
  const result = formatNumber(Math.abs(value), {
    decimalSeparator: decimal.separator,
    precision: !isRealFloat(value) && value >= 10 ? 0 : decimal.length,
    thousandSeparator: group.separator,
    groupLength: group.length,
    abbreviate: false
  });
  const base = patterns.standard;
  const pattern = value < 0 ? base[0] : base[1];

  return pattern.replace('n', result);
};

NumberFormatter.formatPercent = function (value) {
  if (typeof value !== 'number') {
    throw new Error(`first argument must be a number: ${value}`);
  }

  const { group, decimal, patterns } = configuration;
  const result = formatNumber(Math.abs(value), {
    decimalSeparator: decimal.separator,
    precision: decimal.length,
    thousandSeparator: group.separator,
    groupLength: group.length,
    abbreviate: false
  });
  const base = patterns.standard;
  const pattern = value < 0 ? base[0] : base[1];

  return pattern.replace('n', result).replace(new RegExp(`(\\${configuration.decimal.separator}?0+)$`), '');
};

NumberFormatter.format = function (
  value,
  currency,
  abbreviate = false,
  decimalLength = configuration.decimal.length,
  trimDecimalPartWhenPossible = false
) {
  if (typeof value !== 'number') {
    throw new Error(`first argument must be a number: ${value}`);
  }

  const { group, decimal, patterns } = configuration;
  const result = formatNumber(Math.abs(value), {
    decimalSeparator: decimal.separator,
    precision: trimDecimalPartWhenPossible && !isRealFloat(value) ? 0 : decimalLength,
    thousandSeparator: group.separator,
    groupLength: group.length,
    abbreviate
  });
  const base = currency ? patterns.amount : patterns.standard;
  const pattern = value < 0 ? base[0] : base[1];

  return pattern.replace('S', currency || '').replace('n', result);
};

export default NumberFormatter;
